"use client"

import { useEffect, useState, useRef } from "react"
import { auth, db } from "../Firebase/firebase-config"
import { ref, onValue, update, get } from "firebase/database"
import { useNavigate } from "react-router-dom"
import { FaUserEdit, FaSave, FaTrash, FaEllipsisH, FaCog, FaHome } from "react-icons/fa"

import { useAccount } from "../context/AccountContext"
import "./UserProfile.css"
import { MdInfo } from "react-icons/md"

const UserProfile = () => {
  const { currentAccount, accountSwitchTrigger } = useAccount()
  const [profileData, setProfileData] = useState({
    email: "",
    username: "",
    gstin: "",
    pan: "",
    company_name: "",
    phone: "",
  })
  const [editMode, setEditMode] = useState(false)
  const [error, setError] = useState("")
  const [menuOpen, setMenuOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [gstError, setGstError] = useState("")
  const menuRef = useRef(null)
  const navigate = useNavigate()

  // Fetch profile whenever selected account changes
  useEffect(() => {
    if (!currentAccount) return

    const profileRef = ref(db, `profileDashboard/${currentAccount.uid}`)
    const unsubscribe = onValue(profileRef, (snapshot) => {
      if (snapshot.exists()) {
        setProfileData((prev) => ({
          ...prev,
          ...snapshot.val(),
        }))
      } else {
        console.error("Profile data not found.")
        setProfileData({
          email: "",
          username: "",
          gstin: "",
          pan: "",
          company_name: "",
          phone: "",
        })
      }
    })

    return () => unsubscribe()
  }, [currentAccount, accountSwitchTrigger])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false)
        setSettingsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const handleEditToggle = () => {
    setEditMode(!editMode)
    setMenuOpen(false)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    let newValue = value

    // Convert GSTIN and PAN to uppercase
    if (name === "gstin" || name === "pan") newValue = value.toUpperCase()

    setProfileData((prevData) => {
      const updatedData = { ...prevData, [name]: newValue }

      if (name === "gstin") {
        const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{3}$/
        if (newValue.length === 15 && gstRegex.test(newValue)) {
          updatedData.pan = newValue.slice(2, 12) // Extract PAN
          setGstError("")
        } else if (newValue.length === 15 && !gstRegex.test(newValue)) {
          setGstError("Invalid GSTIN format")
        } else {
          setGstError("")
        }
      }

      return updatedData
    })
  }

  const handleSave = async () => {
    if (!currentAccount) return

    try {
      const profileRef = ref(db, `profileDashboard/${currentAccount.uid}`)
      await update(profileRef, {
        username: profileData.username,
        email: profileData.email,
        company_name: profileData.company_name,
        phone: profileData.phone,
        gstin: profileData.gstin || "Not provided",
        pan: profileData.pan || "Not provided",
      })

      setEditMode(false)
      navigate("/dashboard")
    } catch (err) {
      setError("Error updating profile: " + err.message)
    }
  }

  const isProfileIncomplete = () => {
    return (
      !profileData.username?.trim() ||
      !profileData.email?.trim() ||
      !profileData.company_name?.trim() ||
      !profileData.phone?.trim() ||
      !profileData.gstin?.trim() ||
      !profileData.pan?.trim()
    )
  }

  const toggleMenu = (e) => {
    e.stopPropagation()
    setMenuOpen(!menuOpen)
  }

  const toggleSettings = (e) => {
    e.stopPropagation()
    setSettingsOpen(!settingsOpen)
  }

  const home = () => {
    navigate("/dashboard")
  }

  const profileInfo = () => {
    alert(
      "📋 My Profile – Help & Information\n\n" +
      "This page shows and lets you manage your account details.\n\n" +
      "🔹 Why complete your profile?\n" +
      "• Full access to all features (like requesting quotes, creating POs, etc.)\n" +
      "• Accurate GSTIN & PAN are required for billing and compliance\n\n" +
      "🔹 Key Features:\n" +
      "• Edit your username, company name, email, phone, GSTIN & PAN\n" +
      "• GSTIN is validated automatically (15 digits)\n" +
      "• PAN is auto-filled from valid GSTIN (digits 3–12)\n" +
      "• Click the ⋯ menu → Edit Profile to make changes\n" +
      "• Save changes to update your information\n\n" +
      "⚠️ Incomplete profiles will show a warning and may limit some functions.\n\n" +
      "💡 Tip: Keep your details up-to-date for smooth transactions!"
    );
  };
  return (
    <div className="container-fluid profile-Cont">
      <div className="user-profile">
        <div className="profile-menu-container" ref={menuRef}>
          <button className="profile-menu-button" onClick={toggleMenu}>
            <FaEllipsisH />
          </button>

          {menuOpen && (
            <div className="profile-dropdown-menu">
              <div className="profile-menu-item" onClick={handleEditToggle}>
                <FaUserEdit className="profile-menu-icon" />
                <span>{editMode ? "Cancel Edit" : "Edit Profile"}</span>
              </div>
              <div className="profile-menu-item settings-item" onClick={toggleSettings}>
                <FaCog className="profile-menu-icon" />
                <span>Settings</span>
              </div>

              {settingsOpen && (
                <div className="settings-submenu">
                  <div className="profile-menu-item delete-item" onClick={() => navigate("/delete-account")}>
                    <FaTrash className="profile-menu-icon" />
                    <span>Delete Account</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <FaHome className="home-icon" onClick={home} />
        <h2>My Profile
          <MdInfo className="icon-i" style={{marginLeft:'10px'}} onClick={profileInfo} />
        </h2>

        <div className="profile-field">
          <label>
            <strong>Username:</strong>
          </label>
          {editMode ? (
            <input type="text" name="username" value={profileData.username} onChange={handleChange} />
          ) : (
            <span>{profileData.username}</span>
          )}
        </div>

        <div className="profile-field">
          <label>
            <strong>Email:</strong>
          </label>
          {editMode ? (
            <input type="text" name="email" value={profileData.email} onChange={handleChange} />
          ) : (
            <span>{profileData.email}</span>
          )}
        </div>

        <div className="profile-field">
          <label>
            <strong>Company Name:</strong>
          </label>
          {editMode ? (
            <input type="text" name="company_name" value={profileData.company_name} onChange={handleChange} />
          ) : (
            <span>{profileData.company_name}</span>
          )}
        </div>

        <div className="profile-field">
          <label>
            <strong>Phone Number:</strong>
          </label>
          {editMode ? (
            <input type="text" name="phone" value={profileData.phone} onChange={handleChange} />
          ) : (
            <span>{profileData.phone}</span>
          )}
        </div>

        <div className="profile-field">
          <label>
            <strong>GSTIN Number:</strong>
          </label>
          {editMode ? (
            <input
              type="text"
              name="gstin"
              value={profileData.gstin}
              onChange={handleChange}
              maxLength={15}
              placeholder="Enter 15-digit GSTIN"
            />
          ) : (
            <span>{profileData.gstin || "Not Provided"}</span>
          )}
        </div>

        <div className="profile-field pan-number">
          <label>
            <strong>PAN Number:</strong>
          </label>
          {editMode ? (
            <input type="text" name="pan" value={profileData.pan} onChange={handleChange} />
          ) : (
            <span>{profileData.pan || "Not Provided"}</span>
          )}
        </div>
        {gstError && <div className="error-message">{gstError}</div>}

        {editMode && (
          <button className="save-button" onClick={handleSave}>
            <FaSave /> Save Changes
          </button>
        )}
        {isProfileIncomplete() && (
          <div className="incomplete-profile-msg">
            ⚠️ <strong>Please complete your profile to access full features.</strong>
          </div>
        )}
        {error && <div className="error-message">{error}</div>}
      </div>
    </div>
  )
}

export default UserProfile
